import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, User, Settings, LogOut, Sun, Moon, ChevronDown, Check, Clock, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { MOCK_NOTIFICATIONS } from '../../utils/mockData';
interface DashboardHeaderProps {
  title?: string;
}
export function DashboardHeader({
  title
}: DashboardHeaderProps) {
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return localStorage.getItem('renta-theme') as 'light' | 'dark' || 'light';
  });
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);
  const notificationRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.isRead).length;
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setShowProfile(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Apply theme
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('renta-theme', theme);
  }, [theme]);
  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };
  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? {
      ...n,
      isRead: true
    } : n));
  };
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({
      ...n,
      isRead: true
    })));
  };
  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <Check className="h-4 w-4 text-green-600" data-id="element-2245" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-orange-600" data-id="element-2246" />;
      case 'info':
        return <Clock className="h-4 w-4 text-blue-600" data-id="element-2247" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" data-id="element-2248" />;
    }
  };
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  if (!user) return null;
  return <div className="sticky top-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 transition-colors" data-id="element-2249">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8" data-id="element-2250">
        {/* Title (optional) */}
        {title && <h1 className="text-xl font-bold text-gray-900 dark:text-white hidden lg:block" data-id="element-2251">
            {title}
          </h1>}

        {/* Spacer */}
        <div className="flex-1" data-id="element-2252"></div>

        {/* Right side actions */}
        <div className="flex items-center space-x-2 sm:space-x-4" data-id="element-2253">
          {/* Theme Toggle */}
          <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" aria-label="Toggle theme" data-id="element-2254">
            <AnimatePresence mode="wait" data-id="element-2255">
              {theme === 'light' ? <motion.div key="sun" initial={{
              rotate: -90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: 90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2256">
                  <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" data-id="element-2257" />
                </motion.div> : <motion.div key="moon" initial={{
              rotate: 90,
              opacity: 0
            }} animate={{
              rotate: 0,
              opacity: 1
            }} exit={{
              rotate: -90,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-2258">
                  <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" data-id="element-2259" />
                </motion.div>}
            </AnimatePresence>
          </button>

          {/* Notifications */}
          <div className="relative" ref={notificationRef} data-id="element-2260">
            <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" aria-label="Notifications" data-id="element-2261">
              <Bell className="h-5 w-5 text-gray-600 dark:text-gray-400" data-id="element-2262" />
              {unreadCount > 0 && <motion.span initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="absolute top-1 right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center" data-id="element-2263">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </motion.span>}
            </button>

            {/* Notifications Dropdown */}
            <AnimatePresence data-id="element-2264">
              {showNotifications && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-2265">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-2266">
                    <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-2267">
                      Notifications
                    </h3>
                    {unreadCount > 0 && <button onClick={markAllAsRead} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium" data-id="element-2268">
                        Mark all as read
                      </button>}
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-96 overflow-y-auto" data-id="element-2269">
                    {notifications.length > 0 ? notifications.map(notification => <motion.div key={notification.id} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${!notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} onClick={() => markAsRead(notification.id)} data-id="element-2270">
                          <div className="flex items-start justify-between" data-id="element-2271">
                            <div className="flex items-start space-x-3 flex-1" data-id="element-2272">
                              <div className="mt-1" data-id="element-2273">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-2274">
                                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2275">
                                  {notification.title}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2276">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1" data-id="element-2277">
                                  {new Date(notification.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <button onClick={e => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }} className="ml-2 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors" data-id="element-2278">
                              <X className="h-3 w-3 text-gray-400" data-id="element-2279" />
                            </button>
                          </div>
                        </motion.div>) : <div className="p-8 text-center" data-id="element-2280">
                        <Bell className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2281" />
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2282">
                          No notifications
                        </p>
                      </div>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Profile Dropdown */}
          <div className="relative" ref={profileRef} data-id="element-2283">
            <button onClick={() => setShowProfile(!showProfile)} className="flex items-center space-x-2 sm:space-x-3 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-2284">
              <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold border-2 border-blue-200 dark:border-blue-800" data-id="element-2285">
                {user.name.charAt(0)}
              </div>
              <div className="hidden sm:block text-left" data-id="element-2286">
                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2287">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize" data-id="element-2288">
                  {user.role}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 text-gray-400 hidden sm:block" data-id="element-2289" />
            </button>

            {/* Profile Dropdown Menu */}
            <AnimatePresence data-id="element-2290">
              {showProfile && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-2291">
                  {/* User Info */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700" data-id="element-2292">
                    <div className="flex items-center space-x-3" data-id="element-2293">
                      <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-lg border-2 border-blue-200 dark:border-blue-800" data-id="element-2294">
                        {user.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-2295">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate" data-id="element-2296">
                          {user.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-2297">
                          {user.email}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400 capitalize mt-0.5" data-id="element-2298">
                          {user.role} Account
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2" data-id="element-2299">
                    <button onClick={() => {
                  navigate(`/${user.role}/settings`);
                  setShowProfile(false);
                }} className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left" data-id="element-2300">
                      <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-2301" />
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2302">
                        Settings
                      </span>
                    </button>

                    <button onClick={() => {
                  navigate('/help');
                  setShowProfile(false);
                }} className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left" data-id="element-2303">
                      <User className="h-4 w-4 text-gray-500 dark:text-gray-400" data-id="element-2304" />
                      <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2305">
                        Help Center
                      </span>
                    </button>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-200 dark:border-gray-700 py-2" data-id="element-2306">
                    <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left" data-id="element-2307">
                      <LogOut className="h-4 w-4 text-red-600 dark:text-red-400" data-id="element-2308" />
                      <span className="text-sm text-red-600 dark:text-red-400 font-medium" data-id="element-2309">
                        Sign Out
                      </span>
                    </button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}